import React from 'react';
import PropTypes from "prop-types";
import { PanelLeft } from "lucide-react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import { useSelector } from "react-redux";
import { Breadcrumbs } from "../Breadcrumbs";
import Header from "../Header";
import { helpSupportList, sidebarCategories, adminSidebarCategories, mentorSidebarList, menuItems } from "./SidebarData";
import { useSidebarState } from "./hooks/useSidebarState";
import DrawerContent from "./components/DrawerContent";

const drawerWidth = 290;

// Main Sidebar Component
function Sidebar({ handleNavigationAttempt, window, children }) {
  const userId = useSelector((state) => state.auth.id);
  const userType = useSelector((state) => state.auth.type) || localStorage.getItem("userType");

  const getSidebarCategories = () => {
    switch (userType) {
      case "admin":
        return adminSidebarCategories;
      case "mentor":
        return mentorSidebarList;
      case "user":
      default:
        return sidebarCategories;
    }
  };

  const dynamicSidebarCategories = getSidebarCategories();

  const {
    mobileOpen,
    desktopOpen,
    selectedItem,
    expandedCategories,
    handleCategoryClick,
    handleNavigation,
    toggleMobileOpen,
    toggleDesktopOpen,
    setMobileOpen,
    location
  } = useSidebarState(handleNavigationAttempt, dynamicSidebarCategories, helpSupportList);

  const container = window !== undefined ? () => window().document.body : undefined;



  return (
    <Box sx={{ 
      display: "grid", 
      gridTemplateColumns: { 
        xs: "1fr", 
        sm: "1fr", 
        md: desktopOpen ? `${drawerWidth}px 1fr` : "1fr", 
        lg: desktopOpen ? `${drawerWidth}px 1fr` : "1fr" 
      }, 
      height: "100vh" 
    }}>
      {/* Desktop Sidebar */}
      <Box component="nav" sx={{ 
        display: { xs: "none", sm: "none", md: "block" }, 
        "& .MuiDrawer-paper": { 
          boxSizing: "border-box", 
          width: { md: drawerWidth, lg: drawerWidth }, 
          backgroundColor: "#ffffff", 
          color: "#374151", 
          borderBottomRightRadius: "12px", 
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)" 
        } 
      }}>
        <Drawer 
          variant="persistent" 
          open={desktopOpen} 
          onClose={toggleDesktopOpen} 
          sx={{ 
            "& .MuiDrawer-paper": { 
              pt: 0, 
              borderBottomRightRadius: "12px",
              width: { md: drawerWidth, lg: drawerWidth }
            } 
          }}
        >
          <DrawerContent
            mobileOpen={mobileOpen}
            setMobileOpen={setMobileOpen}
            sidebarCategories={dynamicSidebarCategories}
            expandedCategories={expandedCategories}
            selectedItem={selectedItem}
            location={location}
            helpSupportList={helpSupportList}
            handleCategoryClick={handleCategoryClick}
            handleNavigation={handleNavigation}
            userType={userType}
          />
        </Drawer>
      </Box>

      {/* Main Content Area */}
      <Box component="main" sx={{ overflow: "auto" }}>
        
        {/* Header */}
        <div className="relative">
          <Header 
            toggleSidebar={toggleDesktopOpen} 
            sidebarOpen={desktopOpen}
            toggleMobileSidebar={toggleMobileOpen}
            mobileSidebarOpen={mobileOpen}
          />
        </div>

        {/* Mobile Sidebar */}
        <Drawer 
          container={container} 
          variant="temporary" 
          anchor="left" 
          open={mobileOpen} 
          onClose={() => setMobileOpen(false)} 
          ModalProps={{ 
            keepMounted: true,
            disableScrollLock: false
          }}
          transitionDuration={300}
          sx={{ 
            display: { xs: "block", sm: "block", md: "none" }, 
            "& .MuiDrawer-paper": { 
              boxSizing: "border-box", 
              width: { xs: "280px", sm: "300px", md: drawerWidth }, 
              backgroundColor: "#ffffff", 
              color: "#374151", 
              pt: 0, 
              borderBottomRightRadius: "12px", 
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              zIndex: 1300,
              overflow: "hidden"
            } 
          }}
        >
          <DrawerContent
            mobileOpen={mobileOpen}
            setMobileOpen={setMobileOpen}
            sidebarCategories={dynamicSidebarCategories}
            expandedCategories={expandedCategories}
            selectedItem={selectedItem}
            location={location}
            helpSupportList={helpSupportList}
            handleCategoryClick={handleCategoryClick}
            handleNavigation={handleNavigation}
            userType={userType}
          />
        </Drawer>

        {/* Page Content */}
        <div className="p-4 bg-white">
          <Breadcrumbs />
          {children}
        </div>
      </Box>
    </Box>
  );
}

Sidebar.propTypes = {
  handleNavigationAttempt: PropTypes.func,
  window: PropTypes.func,
  children: PropTypes.node,
};

export default Sidebar; 